import moment from 'moment-timezone'
import classnames from 'classnames'
import _ from 'lodash'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resetCurrentDetailTab, setCurrentDetailTab, setIsJoinedModalOpen } from '../../../../reducers/programPresentationReducer'
import useJoinWaitlist from './hooks/useJoinWaitlist'

import ButtonLink from '../../../common/buttons/ButtonLink'
import ProgramPresentationHeader from './ProgramPresentationHeader'
import ProgramDescription from './PresentationTabs/ProgramDescription'
import ProgramSchedule from './PresentationTabs/ProgramSchedule'
import ProgramPricing from './PresentationTabs/ProgramPricing'
import SeriesSchedule from './PresentationTabs/SeriesSchedule'
import ProgramActionsCard from './ProgramActionsCard'
import JoinWaitlistButton from './JoinWaitlistButton'
import AddStudentsWaitlistModal from './modals/AddStudentsWaitlistModal'
import RegistrationAuthModal from './modals/RegistrationAuthModal'
import SuccessModal from '../../../common/customModal/SuccessModal'

import styles from './ProgramPresentationPage.module.scss'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon.svg'
import { ReactComponent as EditIcon } from '../../../../assets/images/common/icons/edit-icon.svg'

export const RegisterButton = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const course = useSelector(state => state.programPresentation.course)
  const user = useSelector(state => state.user.current_user)

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const isUnauthenticated = _.isEmpty(user)

  const registrationClosedMessage = useMemo(
    () => {
      switch (course.registrationClosedReason) {
        case 'limit_reached':
          return t('programs.landing.registerButton.limitReached')
        case 'registration_closed':
          return t('programs.landing.registerButton.registrationWindow', {
            start: moment(course?.registrationStartsAt)
              .tz(course?.community?.timezone)
              .format('L LT'),
            end: moment(course?.registrationEndsAt)
              .tz(course?.community?.timezone)
              .format('L LT'),
          })
        default:
          return ''
      }
    }, [course, t]
  )

  const handleRegisterClick = (e) => {
    if (course.previewMode) {
      e.preventDefault()
      return
    }

    if (isUnauthenticated) {
      e.preventDefault()
      setIsAuthModalOpen(true)
    }
  }

  const handleLogin = () => navigate('register', { state: { intent: 'login' } })
  const handleCreateAccount = () => navigate('register', { state: { intent: 'signup' } })

  return (
    course?.registrationClosedReason ? (
      <div className={classnames('w-100 d-flex justify-content-center align-items-center', styles['registration-closed-placeholder'])}>
        <InfoCircleIcon className={styles['info-circle-icon']} />
        <p className={styles['registration-closed-message']}>
          {registrationClosedMessage}
        </p>
      </div>
    ) : (
      <>
        <ButtonLink navigateTo='register' noTopMargin aria-hidden='true' onClick={handleRegisterClick}>
          {t('programs.landing.registerButton.registerNow')}
        </ButtonLink>

        <RegistrationAuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          onCreateAccount={handleCreateAccount}
          onLogin={handleLogin}
        />
      </>
    )
  )
}

const ProgramSectionSelector = forwardRef((_, ref) => {
  const currentDetailTab = useSelector(state => state.programPresentation.currentDetailTab)

  return (
    <div ref={ref} className={styles['program-presentation-section']}>
      {currentDetailTab === 'Description' && <ProgramDescription />
        || currentDetailTab === 'Schedule' && <ProgramSchedule />
        || currentDetailTab === '9 Hole Events' && <SeriesSchedule />
        || currentDetailTab === 'Pricing' && <ProgramPricing />}
    </div>
  )
})

ProgramSectionSelector.displayName = 'ProgramSectionSelector'

const detailTabKeys = {
  'Description': 'common.field.description',
  'Schedule': 'programs.landing.tabs.schedule',
  '9 Hole Events': 'programs.landing.tabs.nineHoleEvents',
  'Pricing': 'programs.landing.tabs.pricing',
}

const ProgramPresentationPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isWaitlistModalOpen = useSelector(state => state.programPresentation.isWaitlistModalOpen)
  const isJoinedModalOpen = useSelector(state => state.programPresentation.isJoinedModalOpen)
  const { handleJoinButtonClick, handleAddStudentsToWaitlist } = useJoinWaitlist()

  const user = useSelector(state => state.user.current_user)
  const { course, detailsTabs, currentDetailTab } = useSelector(state => state.programPresentation)

  const ref = useRef(null)

  const editVisible = user && user.type === 'Coach' && user.communities?.[0]?.id === course.community.id && !course.previewMode

  useEffect(() => () => {
    dispatch(resetCurrentDetailTab())
  }, [dispatch])

  const handleTabButtonClick = (newTab) => {
    if (newTab !== currentDetailTab) {
      dispatch(setCurrentDetailTab(newTab))
      ref.current.scrollTo(0, 0)
    }
  }

  return (
    <div className={
      classnames(
        'd-flex flex-grow-1 flex-column',
        styles['program-presentation-page'],
        { [styles['preview-mode']]: course.previewMode }
      )
    }>
      <ProgramPresentationHeader className='d-none d-lg-flex' />
      <div className={styles['details-tab-bar-wrapper']}>
        <ul className={classnames('h-100 p-0 d-flex', styles['details-tab-bar'])}>
          {detailsTabs.map(detailTab => (
            <li key={detailTab} className={styles['detail-tab']}>
              <button
                className={classnames(styles['detail-tab-button'], { [styles['current-tab']]: currentDetailTab === detailTab })}
                onClick={() => handleTabButtonClick(detailTab)}
              >
                {detailTabKeys[detailTab] ? t(detailTabKeys[detailTab]) : detailTab}
              </button>
            </li>
          ))
          }
        </ul>
      </div>
      <ProgramSectionSelector ref={ref} />

      <div className={classnames('d-none d-lg-flex justify-content-center', styles['actions-card-wrapper'], { [styles.coachLayout]: editVisible })}>
        <div className={classnames('d-flex flex-column align-items-end', styles.actionsWrapper)}>
          {editVisible && (
            <ButtonLink navigateTo={`/programs/${course.id}/edit`} className={classnames('mb-2 px-3', styles.editLink)}>
              <div className='d-flex align-items-center'>
                <span className='mr-2 font-weight-bold'>{t('programs.common.editProgram')}</span>
                <EditIcon aria-hidden='true' className={styles.editIcon} />
              </div>
            </ButtonLink>
          )}

          <ProgramActionsCard className={classnames('d-flex', styles['actions-card'])} />
        </div>
      </div>
      <div className={classnames('d-flex flex-column d-lg-none justify-content-center', styles['program-presentation-footer'])}>
        {course.canStudentsJoinWaitlist
          ? <JoinWaitlistButton onJoinButtonClick={handleJoinButtonClick} />
          : <RegisterButton />}
        <ButtonLink navigateTo={`/landing/${course.community.id}`} linkColor='dark-navy'>
          {t('common.action.goBack')}
        </ButtonLink>
      </div>
      <SuccessModal
        isOpen={isJoinedModalOpen}
        onClose={() => dispatch(setIsJoinedModalOpen(false))}
        title={t('programs.landing.joinedWaitlistModal.title')}
        message={t('programs.landing.joinedWaitlistModal.message')}
        buttonMessage={t('common.close')}
      />
      {isWaitlistModalOpen && (
        <AddStudentsWaitlistModal handleAddStudentsToWaitlist={handleAddStudentsToWaitlist} />
      )}
      <ToastContainer theme='colored' />
    </div>
  )
}

export default ProgramPresentationPage
